import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  ArrowLeft, Network, AlertCircle, CheckCircle, XCircle,
  Server, Wifi, Users, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Infrastructure Node Component
const InfrastructureNode = ({ data }) => {
  const getNodeIcon = () => {
    if (data.type === 'core_router') return <Server className="h-8 w-8" />;
    if (data.type === 'distribution_switch') return <Network className="h-6 w-6" />;
    return <Wifi className="h-5 w-5" />;
  };

  const getNodeColor = () => {
    if (data.status === 'active') return 'bg-gradient-to-br from-blue-500 to-indigo-600';
    return 'bg-gradient-to-br from-gray-400 to-gray-600';
  };

  return (
    <div className={`${getNodeColor()} text-white rounded-lg shadow-lg p-4 min-w-[180px] border-2 border-white`}>
      <div className="flex items-center gap-2 mb-2">
        {getNodeIcon()}
        <div className="font-bold text-sm">{data.label}</div>
      </div>
      <div className="text-xs space-y-1">
        <div className="flex items-center gap-1">
          <span className="opacity-80">Location:</span>
          <span>{data.location}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="opacity-80">IP:</span>
          <span className="font-mono">{data.ip_address}</span>
        </div>
        {data.capacity && (
          <div className="flex items-center gap-1">
            <span className="opacity-80">Capacity:</span>
            <span>{data.capacity} clients</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Customer Node Component
const CustomerNode = ({ data }) => {
  const getStatusColor = () => {
    if (data.status === 'active') return 'border-green-500 bg-green-50';
    if (data.status === 'inactive') return 'border-red-500 bg-red-50';
    return 'border-gray-400 bg-gray-50';
  };

  const getStatusIcon = () => {
    if (data.status === 'active') return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (data.status === 'inactive') return <XCircle className="h-4 w-4 text-red-600" />;
    return <AlertCircle className="h-4 w-4 text-gray-600" />;
  };

  return (
    <div className={`${getStatusColor()} rounded-md shadow border-2 p-2 min-w-[140px]`}>
      <div className="flex items-center gap-2 mb-1">
        {getStatusIcon()}
        <div className="font-medium text-xs text-gray-900">{data.label}</div>
      </div>
      <div className="text-xs text-gray-600">
        <div className="font-mono">{data.ip_address}</div>
      </div>
    </div>
  );
};

const nodeTypes = {
  infrastructureNode: InfrastructureNode,
  customerNode: CustomerNode
};

const NetworkTopologyView = () => {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeDetails, setNodeDetails] = useState(null);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [impactAnalysis, setImpactAnalysis] = useState(null);

  useEffect(() => {
    fetchTopologyData();
  }, []);

  const fetchTopologyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/topology`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const topology = data.topology;
        
        setNodes(topology.nodes);
        setEdges(topology.edges);
        setStatistics(topology.statistics);
      } else {
        toast.error('Failed to load topology');
      }
    } catch (error) {
      console.error('Error fetching topology:', error);
      toast.error('Error loading network topology');
    } finally {
      setLoading(false);
    }
  };

  const onNodeClick = useCallback(async (event, node) => {
    setSelectedNode(node);
    
    // Fetch node details
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/topology/node/${node.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setNodeDetails(data.details);
      }
    } catch (error) {
      console.error('Error fetching node details:', error);
    }
  }, []);

  const simulateFailure = async (nodeId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/topology/simulate-failure/${nodeId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setImpactAnalysis(data.impact);
        setShowImpactModal(true);
      }
    } catch (error) {
      console.error('Error simulating failure:', error);
      toast.error('Failed to simulate node failure');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Network className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading network topology...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-blue-700 text-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Network className="h-8 w-8" />
                Network Topology
              </h1>
              <p className="text-blue-100 mt-1">Interactive network infrastructure visualization</p>
            </div>
          </div>
          <Button
            onClick={fetchTopologyData}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Statistics Cards */}
        <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Network Nodes</p>
                  <p className="text-3xl font-bold">{statistics?.total_nodes || 0}</p>
                </div>
                <Server className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Active Customers</p>
                  <p className="text-3xl font-bold">{statistics?.active_customers || 0}</p>
                </div>
                <CheckCircle className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Inactive Customers</p>
                  <p className="text-3xl font-bold">{statistics?.inactive_customers || 0}</p>
                </div>
                <XCircle className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Total Customers</p>
                  <p className="text-3xl font-bold">{statistics?.total_customers || 0}</p>
                </div>
                <Users className="h-10 w-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Topology Diagram */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>Network Infrastructure Map</CardTitle>
              <p className="text-sm text-gray-500">Click on any node to view details</p>
            </CardHeader>
            <CardContent className="p-0">
              <div style={{ height: '700px' }}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  nodeTypes={nodeTypes}
                  fitView
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    animated: true,
                    markerEnd: {
                      type: MarkerType.ArrowClosed,
                      width: 20,
                      height: 20
                    }
                  }}
                >
                  <Background color="#aaa" gap={16} />
                  <Controls />
                  <MiniMap
                    nodeColor={(node) => {
                      if (node.type === 'infrastructureNode') return '#3b82f6';
                      if (node.data.status === 'active') return '#10b981';
                      return '#ef4444';
                    }}
                  />
                </ReactFlow>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Node Details Panel */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Node Details</CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedNode ? (
                <div className="text-center text-gray-400 py-8">
                  <Network className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Click on a node to view details</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-bold text-lg mb-2">{selectedNode.data.label}</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <span className="ml-2 font-medium">
                          {selectedNode.data.type === 'core_router' && 'Core Router'}
                          {selectedNode.data.type === 'distribution_switch' && 'Distribution Switch'}
                          {selectedNode.data.type === 'customer' && 'Customer'}
                        </span>
                      </div>
                      
                      {selectedNode.data.location && (
                        <div>
                          <span className="text-gray-500">Location:</span>
                          <span className="ml-2 font-medium">{selectedNode.data.location}</span>
                        </div>
                      )}
                      
                      <div>
                        <span className="text-gray-500">IP Address:</span>
                        <span className="ml-2 font-mono font-medium">{selectedNode.data.ip_address}</span>
                      </div>

                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className={`ml-2 font-medium ${
                          selectedNode.data.status === 'active' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {selectedNode.data.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {nodeDetails?.connected_customers !== undefined && (
                    <div className="border-t pt-3">
                      <h4 className="font-medium mb-2">Connected Customers</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Total:</span>
                          <span className="font-medium">{nodeDetails.connected_customers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Active:</span>
                          <span className="font-medium text-green-600">{nodeDetails.active_customers}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedNode.type === 'infrastructureNode' && (
                    <div className="border-t pt-3">
                      <Button
                        onClick={() => simulateFailure(selectedNode.id)}
                        variant="destructive"
                        size="sm"
                        className="w-full"
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Simulate Failure
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Impact Analysis Modal */}
      {showImpactModal && impactAnalysis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-red-600" />
                Impact Analysis: Node Failure Simulation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-bold text-red-900 mb-2">Node: {impactAnalysis.node_id}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Affected:</span>
                      <span className="ml-2 font-bold text-red-600">{impactAnalysis.total_affected}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Active Affected:</span>
                      <span className="ml-2 font-bold text-red-600">{impactAnalysis.active_affected}</span>
                    </div>
                  </div>
                </div>

                {impactAnalysis.affected_nodes?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Affected Network Nodes:</h4>
                    <div className="space-y-2">
                      {impactAnalysis.affected_nodes.map((node) => (
                        <div key={node.node_id} className="bg-gray-50 p-2 rounded text-sm">
                          {node.node_name} ({node.node_id})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Affected Customers:</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {impactAnalysis.affected_customers?.map((customer) => (
                      <div key={customer.id} className="bg-gray-50 p-2 rounded text-sm flex items-center justify-between">
                        <span>{customer.customer_name}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          customer.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {customer.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => setShowImpactModal(false)} className="flex-1">
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NetworkTopologyView;
